import { ChipToggleGroup, ChipToggleOption } from '@app/components/chip-toggle-group';
import { Box, Theme, useTheme } from '@mui/material';
import { MessageDescriptor, useIntl } from 'react-intl';
import { ScheduleStatusCounts } from './get-schedule-status-counts';
import {
  SCHEDULE_STATUS_FILTER_ORDER,
  ScheduleStatusFilterValue,
  ScheduleStatusKind,
} from './get-schedule-status-kind';
import {
  scheduleStatusFilterMessages,
  scheduleStatusOverviewAllMessage,
} from './schedule-status-spot-messages';
import { ScheduleStatusHelpTooltip } from './ScheduleStatusHelpTooltip';

const ALL_FILTER_VALUE = 'all';

export type ScheduleStatusOverviewValue = ScheduleStatusFilterValue | null;

interface ScheduleStatusSpotOverviewProps {
  counts: ScheduleStatusCounts;
  selected: ScheduleStatusOverviewValue;
  onSelect: (value: ScheduleStatusOverviewValue) => void;
  /** When false, hides the overlapping off-tolerance filter (PNT has no tolerance). */
  includeOffTolerance?: boolean;
  helpMessage?: MessageDescriptor;
}

/** Theme color matching the coverage chip type for each overview bucket. */
function getOverviewChipColor(theme: Theme, value: ScheduleStatusOverviewValue): string {
  if (value == null) {
    return theme.palette.primary.main;
  }

  if (value === 'offTolerance') {
    return theme.palette.warning.main;
  }

  const kindColors: Record<ScheduleStatusKind, string> = {
    missingEmission: theme.palette.error.main,
    none: theme.palette.grey[500],
    complete: theme.palette.success.main,
    partial: theme.palette.warning.main,
  };

  return kindColors[value];
}

/**
 * Filterable coverage totals above the table. Off-tolerance is listed separately
 * because it overlaps Completo/Parcial.
 */
export function ScheduleStatusSpotOverview({
  counts,
  selected,
  onSelect,
  includeOffTolerance = true,
  helpMessage,
}: ScheduleStatusSpotOverviewProps) {
  const { $t } = useIntl();
  const theme = useTheme();

  const options: ChipToggleOption[] = [
    {
      value: ALL_FILTER_VALUE,
      label: `${$t(scheduleStatusOverviewAllMessage)} (${counts.total})`,
      chipColor: getOverviewChipColor(theme, null),
      filledLabelColor: '#fff',
    },
    ...SCHEDULE_STATUS_FILTER_ORDER.filter((value) => {
      if (value === 'offTolerance' && !includeOffTolerance) {
        return false;
      }

      return value !== 'none' || counts.byFilter.none > 0;
    }).map((value) => ({
      value,
      label: `${$t(scheduleStatusFilterMessages[value])} (${counts.byFilter[value]})`,
      chipColor: getOverviewChipColor(theme, value),
      filledLabelColor: '#fff',
      disabled: counts.byFilter[value] === 0,
    })),
  ];

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
      <ChipToggleGroup
        options={options}
        value={selected ?? ALL_FILTER_VALUE}
        allowDeselect
        deselectValue={ALL_FILTER_VALUE}
        onChange={(next) =>
          onSelect(next === ALL_FILTER_VALUE ? null : (next as ScheduleStatusFilterValue))
        }
        chipSize="medium"
      />
      <ScheduleStatusHelpTooltip message={helpMessage} />
    </Box>
  );
}
